/**
 * Core helper function that finds an item by its string value and logs an error if not found
 */
const findItemByValue = <Item>(
  value: string,
  items: Item[],
  itemToStringValue: (item: Item) => string
): Item | undefined => {
  // Sometimes items are loaded async and for a moment they will be empty. In those cases we don't think it's necessary to log errors in console.
  if (items.length === 0) return;

  const foundItem = items.find((item) => itemToStringValue(item) === value);

  if (foundItem === undefined) {
    console.error(
      'Was not able to find a matching item for the provided value. This could happen if the items provided to the component are changing over time and the provided value no longer matches any item.',
      `Provided value: ${value}`,
      'Items:',
      items
    );
  }

  return foundItem;
};

/**
 * Gets the selected items from the provided values array
 */
export const getSelectedItems = <Item>(
  values: string[],
  items: Item[],
  itemToStringValue: (item: Item) => string
): Item[] => {
  const result = values.map((stringValue) =>
    findItemByValue(stringValue, items, itemToStringValue)
  );

  // only return items, don't need undefined values
  return result.filter((item) => item !== undefined);
};

/**
 * Gets a single item by its string value, with support for null/undefined values
 */
export const getItemByValue = <Item>(
  value: string | null | undefined,
  items: Item[],
  itemToStringValue: (item: Item) => string
): Item | null | undefined => {
  if (value == null) return value;

  return findItemByValue(value, items, itemToStringValue);
};
